import React, { useState, useEffect } from "react";
import { Form, Spinner, Toast, ToastContainer } from "react-bootstrap";
import { ReactComponent as BackIc } from "../../../assets/svg/back_arrow.svg";
import { ReactComponent as DeleteIc } from "../../../assets/svg/delete_btn.svg";
import { ReactComponent as DownArrowIc } from "../../../assets/svg/down_arrow_ics.svg";
import { apiCall, apiCallAdmin } from "../../../services/api-service.js";
import "./approval-create.css";

export const ApprovalConfigCreate = ({ onBack, editData }) => {
    const [levels, setLevels] = useState([]);
    const [code, setCode] = useState("");
    const [name, setName] = useState("");
    const [type, setType] = useState("Sequential");
    const [module, setModule] = useState("");
    const [status, setStatus] = useState("Active");
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarStatus, setSnackbarStatus] = useState(false);

    const handleSnackbarClose = () => setShowSnackbar(false);

    const showToast = (message, status = false) => {
        setSnackbarMessage(message);
        setSnackbarStatus(status);
        setShowSnackbar(true);
    };

    const [usersList, setUsersList] = useState([]);
    const [rolesList, setRolesList] = useState([]);
    const [searchVal, setSearchVal] = useState("");
    const [activeDropdown, setActiveDropdown] = useState({ levelId: null, type: null });
    const [dropdownDirection, setDropdownDirection] = useState("down");

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const usersRes = await apiCallAdmin("get", "get_user");
                if (usersRes?.status === 200 && usersRes.data?.success) {
                    setUsersList(usersRes.data.suggestionlist || []);
                }
                const rolesRes = await apiCallAdmin("get", "get_role");
                if (rolesRes?.status === 200 && rolesRes.data?.success) {
                    setRolesList(rolesRes.data.suggestionlist || []);
                }
            } catch (error) {
                // Silent catch
            }
        };
        loadInitialData();
    }, []);

    useEffect(() => {
        if (editData) {
            setCode(editData.code || "");
            setName(editData.name || "");
            setType(editData.type || "Sequential");
            setModule(editData.module || "");
            setStatus(editData.status || "Active");

            if (editData.levels) {
                const mappedLevels = editData.levels.map(lvl => {
                    const persons = (lvl.approvers || []).map(appr => {
                        if (appr.role_id) {
                            const roleObj = rolesList.find(r => String(r.value) === String(appr.role_id));
                            if (roleObj) {
                                return { value: roleObj.value, label: roleObj.label };
                            }
                            if (appr.role) {
                                return {
                                    value: appr.role_id,
                                    label: appr.role.role_name || appr.role.name || appr.role.label || `Role ${appr.role_id}`
                                };
                            }
                            return { value: appr.role_id, label: `Role ${appr.role_id}` };
                        } else if (appr.user_id) {
                            const userObj = usersList.find(u => String(u.value) === String(appr.user_id));
                            if (userObj) {
                                return { value: userObj.value, label: userObj.label };
                            }
                            if (appr.user) {
                                return {
                                    value: appr.user_id,
                                    label: appr.user.first_name || appr.user.name || appr.user.label || `User ${appr.user_id}`
                                };
                            }
                            return { value: appr.user_id, label: `User ${appr.user_id}` };
                        }
                        return null;
                    }).filter(Boolean);

                    const levelType = lvl.approvers?.[0]?.role_id
                        ? "Role based"
                        : (lvl.level_name?.toLowerCase().includes("reporting") ? "Reporting Manager" : "Specific user");

                    return {
                        id: lvl.id || Date.now() + Math.random(),
                        name: lvl.level_name || "",
                        type: levelType,
                        persons: persons,
                        canReject: !!lvl.can_reject,
                        commentsMandatory: !!lvl.comments_mandatory
                    };
                });
                setLevels(mappedLevels);
            }
        }
    }, [editData, usersList, rolesList]);

    const handleDropdownOpen = (levelId, type, event) => {
        setActiveDropdown({ levelId, type });
        setSearchVal("");

        if (event) {
            const rect = event.currentTarget.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            if (spaceBelow < 220) {
                setDropdownDirection("up");
            } else {
                setDropdownDirection("down");
            }
        } else {
            setDropdownDirection("down");
        }
    };

    const getFilteredSuggestions = (level) => {
        if (!level.type) return [];
        const list = (level.type === "Specific user" || level.type === "Reporting Manager") ? usersList : rolesList;
        return list.filter(item =>
            item.label.toLowerCase().includes(searchVal.toLowerCase()) &&
            !level.persons.some(p => p.value === item.value)
        );
    };

    const addPerson = (levelId, person) => {
        setLevels(levels.map(l => {
            if (l.id === levelId) {
                if (l.persons.some(p => p.value === person.value)) return l;
                return { ...l, persons: [...l.persons, person] };
            }
            return l;
        }));
        setSearchVal("");
        if (errors[`level_persons_${levelId}`]) {
            setErrors(prev => ({ ...prev, [`level_persons_${levelId}`]: "" }));
        }
    };

    const addLevel = () => {
        setLevels([...levels, { id: Date.now(), name: "", type: "", persons: [], canReject: false, commentsMandatory: false }]);
    };

    const removeLevel = (id) => {
        const filtered = levels.filter(l => l.id !== id);
        setLevels(filtered);
    };

    const removePerson = (levelId, person) => {
        setLevels(levels.map(l => {
            if (l.id === levelId) {
                return { ...l, persons: l.persons.filter(p => p.value !== person.value) };
            }
            return l;
        }));
    };

    const handleSave = async () => {
        setErrors({});
        const newErrors = {};

        if (!code.trim()) {
            newErrors.code = "Code is required";
        }
        if (!name.trim()) {
            newErrors.name = "Name is required";
        }
        if (!module) {
            newErrors.module = "Module is required";
        }

        for (let i = 0; i < levels.length; i++) {
            const lvl = levels[i];
            if (!lvl.name.trim()) {
                newErrors[`level_name_${lvl.id}`] = "Level Name is required";
            }
            if (!lvl.type) {
                newErrors[`level_type_${lvl.id}`] = "Approver Type is required";
            }
            if (lvl.persons.length === 0) {
                newErrors[`level_persons_${lvl.id}`] = "At least one approver is required";
            }
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            showToast("Please fill all required fields");
            return;
        }

        if (levels.length === 0) {
            showToast("At least one approval level is required");
            return;
        }

        setLoading(true);
        try {
            const payloadLevels = levels.map((lvl, index) => {
                const approvers = lvl.persons.map(p => {
                    if (lvl.type === "Role based") {
                        return { role_id: p.value, user_id: null };
                    } else {
                        return { user_id: p.value, role_id: null };
                    }
                });

                return {
                    level_name: lvl.name,
                    level_order: index + 1,
                    approver_type: type === "Sequential" ? "Sequence" : "Parallel",
                    can_reject: lvl.canReject,
                    comments_mandatory: lvl.commentsMandatory,
                    approvers: approvers
                };
            });

            const payload = {
                code: code.trim(),
                name: name.trim(),
                type: type,
                module: module,
                levels_count: levels.length,
                status: status,
                levels: payloadLevels
            };

            const response = editData
                ? await apiCall("put", `approval_configurations/${editData.id}`, payload)
                : await apiCall("post", "approval_configurations", payload);
            if (response?.status === 200 && response.data?.success) {
                showToast(editData ? "Approval Configuration Updated Successfully" : "Approval Configuration Saved Successfully", true);
                setTimeout(() => {
                    onBack();
                }, 1500);
            } else {
                const backendErrors = response.data?.error || {};
                const mappedErrors = {};
                Object.keys(backendErrors).forEach(key => {
                    const errVal = backendErrors[key];
                    mappedErrors[key] = Array.isArray(errVal) ? errVal[0] : errVal;
                });

                Object.keys(backendErrors).forEach(key => {
                    if (key.startsWith('levels.')) {
                        const parts = key.split('.');
                        const lvlIndex = parseInt(parts[1], 10);
                        const field = parts[2];
                        if (levels[lvlIndex]) {
                            const lvlId = levels[lvlIndex].id;
                            mappedErrors[`level_${field}_${lvlId}`] = Array.isArray(backendErrors[key]) ? backendErrors[key][0] : backendErrors[key];
                        }
                    }
                });

                setErrors(mappedErrors);
                const errMsg = response.data?.message || "Failed to save approval configuration";
                showToast(errMsg);
            }
        } catch (error) {
            showToast("An error occurred while saving");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest(".custom-multiselect") && !event.target.closest(".custom-dropdown-menu")) {
                setActiveDropdown({ levelId: null, type: null });
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <div className="approval-config-container p-0">
            {/* Top Header */}
            <div className="bg-white d-flex align-items-center justify-content-between p-3 mb-3 approval-top-header">
                <div className="d-flex align-items-center gap-3">
                    <div className="cursor-pointer" onClick={onBack}>
                        <BackIc width={20} height={20} />
                    </div>
                    <h5 className="m-0 fw-700 fs-18 header-title">{editData ? "Edit approval configuration" : "New approval configuration"}</h5>
                </div>
                <div className="d-flex gap-3">
                    <button className="btn border-0 fw-700 fs-14 px-4 btn-cancel-custom"
                        onClick={onBack}
                        disabled={loading}>Cancel</button>
                    <button
                        className="btn fw-700 fs-14 px-4 text-white d-flex align-items-center justify-content-center gap-2 btn-save-custom"
                        disabled={loading}
                        onClick={handleSave}
                    >
                        {loading ? (
                            <>
                                <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
                                Saving...
                            </>
                        ) : (
                            "Save"
                        )}
                    </button>
                </div>
            </div>

            {/* Main Outer Card Wrapping Both Sections */}
            <div className="bg-white main-outer-card p-4 mb-3">

                {/* Section 1: Details Section (948px width with white space on the right) */}
                <div className="details-section-wrapper">
                    <h6 className="form-section-title mb-4">Approval configuration details</h6>

                    <div className="row gx-5 gy-3">
                        <div className="col-md-6 d-flex flex-column align-items-start gap-1">
                            <div className="d-flex align-items-center gap-4 w-100">
                                <label className="label-width">Code <span className="text-danger">*</span></label>
                                <input type="text" className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                                    value={code}
                                    onChange={(e) => {
                                        setCode(e.target.value);
                                        if (errors.code) setErrors(prev => ({ ...prev, code: "" }));
                                    }} />
                            </div>
                            {errors.code && (
                                <div className="text-danger fs-12 fw-500" style={{ marginLeft: "124px" }}>{errors.code}</div>
                            )}
                        </div>
                        <div className="col-md-6 d-flex flex-column align-items-start gap-1">
                            <div className="d-flex align-items-center gap-4 w-100">
                                <label className="label-width">Name <span className="text-danger">*</span></label>
                                <input type="text" className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                    value={name}
                                    onChange={(e) => {
                                        setName(e.target.value);
                                        if (errors.name) setErrors(prev => ({ ...prev, name: "" }));
                                    }} />
                            </div>
                            {errors.name && (
                                <div className="text-danger fs-12 fw-500" style={{ marginLeft: "124px" }}>{errors.name}</div>
                            )}
                        </div>

                        <div className="col-md-6 d-flex align-items-center gap-4">
                            <label className="label-width">Type</label>
                            <Form.Select className="form-select"
                                value={type}
                                onChange={(e) => setType(e.target.value)}>
                                <option value="Sequential">Sequential</option>
                                <option value="Parallel">Parallel</option>
                            </Form.Select>
                        </div>
                        <div className="col-md-6 d-flex flex-column align-items-start gap-1">
                            <div className="d-flex align-items-center gap-4 w-100">
                                <label className="label-width">Module <span className="text-danger">*</span></label>
                                <Form.Select className={`form-select ${errors.module ? 'is-invalid' : ''}`}
                                    value={module}
                                    onChange={(e) => {
                                        setModule(e.target.value);
                                        if (errors.module) setErrors(prev => ({ ...prev, module: "" }));
                                    }}>
                                    <option value="">Select module</option>
                                    <option value="purchase_order">purchase order</option>
                                    <option value="purchase_quotation">Purchase quotation</option>
                                    <option value="transaction">Transaction</option>
                                </Form.Select>
                            </div>
                            {errors.module && (
                                <div className="text-danger fs-12 fw-500" style={{ marginLeft: "124px" }}>{errors.module}</div>
                            )}
                        </div>

                        <div className="col-md-6 d-flex align-items-center gap-4">
                            <label className="label-width">Levels</label>
                            <input type="text" className="form-control"
                                value={levels.length}
                                disabled />
                        </div>
                        <div className="col-md-6 d-flex align-items-center gap-4">
                            <label className="label-width">Status</label>
                            <Form.Select className="form-select"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}>
                                <option value="Active">🟢 Active</option>
                                <option value="Inactive">🔴 Inactive</option>
                            </Form.Select>
                        </div>
                    </div>
                </div>

                {/* Configure Flow Section */}
                <div className="mt-5 p-4 configure-flow-section">
                    {levels.length === 0 ? (
                        <div className="d-flex align-items-center justify-content-center empty-levels-container">
                            <button className="add-level-btn" onClick={addLevel}>
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7 1V13M1 7H13" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Add level
                            </button>
                        </div>
                    ) : (
                        <>
                            <p className="fs-12 fw-700 text-uppercase textGrey mb-4 flow-title-text">CONFIGURE APPROVAL FLOW :-</p>

                            <div className="approval-flow-grid-row mb-3 px-2">
                                <div className="fs-12 fw-700 textGrey text-start">Level Name <span className="text-danger">*</span></div>
                                <div className="fs-12 fw-700 textGrey text-start">Approver Type <span className="text-danger">*</span></div>
                                <div className="fs-12 fw-700 textGrey text-start">Approval Person(s) <span className="text-danger">*</span></div>
                                <div className="fs-12 fw-700 textGrey text-center">Can Reject</div>
                                <div className="fs-12 fw-700 textGrey text-center">Comments Mandatory</div>
                                <div></div>
                            </div>

                            <div className="d-flex flex-column gap-3">
                                {levels.map((level, idx) => {


                                    return (
                                        <div key={level.id} className="approval-flow-grid-row px-2">
                                            <div className="position-relative">
                                                <input
                                                    type="text"
                                                    className={`form-control bg-white level-name-input ${errors[`level_name_${level.id}`] ? 'is-invalid' : ''}`}
                                                    value={level.name}
                                                    placeholder="Enter here"
                                                    onChange={(e) => {
                                                        const updated = [...levels];
                                                        updated[idx].name = e.target.value;
                                                        setLevels(updated);
                                                        if (errors[`level_name_${level.id}`]) {
                                                            setErrors(prev => ({ ...prev, [`level_name_${level.id}`]: "" }));
                                                        }
                                                    }}
                                                />
                                                {errors[`level_name_${level.id}`] && (
                                                    <div className="text-danger fs-11 fw-500 mt-1 px-1">{errors[`level_name_${level.id}`]}</div>
                                                )}
                                            </div>
                                            <div>
                                                <Form.Select
                                                    className={`form-select bg-white level-type-select ${errors[`level_type_${level.id}`] ? 'is-invalid' : ''}`}
                                                    value={level.type}
                                                    onChange={(e) => {
                                                        const updated = [...levels];
                                                        updated[idx].type = e.target.value;
                                                        updated[idx].persons = [];
                                                        setLevels(updated);
                                                        if (errors[`level_type_${level.id}`]) {
                                                            setErrors(prev => ({ ...prev, [`level_type_${level.id}`]: "" }));
                                                        }
                                                    }}
                                                >
                                                    <option value="">Select type</option>
                                                    <option value="Specific user">Specific user</option>
                                                    <option value="Role based">Role based</option>
                                                    <option value="Reporting Manager">Reporting Manager</option>
                                                </Form.Select>
                                                {errors[`level_type_${level.id}`] && (
                                                    <div className="text-danger fs-11 fw-500 mt-1 px-1">{errors[`level_type_${level.id}`]}</div>
                                                )}
                                            </div>
                                            <div className="position-relative">
                                                <div className={`custom-multiselect ${errors[`level_persons_${level.id}`] ? 'border-danger' : ''}`} onClick={(e) => handleDropdownOpen(level.id, 'persons', e)}>
                                                    <div className="d-flex align-items-center gap-2 flex-wrap flex-grow-1 multiselect-pills-container">
                                                        {level.persons.map((p) => (
                                                            <div key={p.value} className="custom-pill">
                                                                {p.label}
                                                                <span className="custom-pill-close" onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    removePerson(level.id, p);
                                                                }}>×</span>
                                                            </div>
                                                        ))}
                                                        <input
                                                            type="text"
                                                            className="multiselect-search-input"
                                                            placeholder={level.persons.length > 0 ? "" : (level.type === "Role based" ? "Select role" : "Select person")}
                                                            value={activeDropdown.levelId === level.id && activeDropdown.type === 'persons' ? searchVal : ""}
                                                            onChange={(e) => setSearchVal(e.target.value)}
                                                            onFocus={(e) => handleDropdownOpen(level.id, 'persons', e)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </div>
                                                    <div className="ps-2 d-flex align-items-center justify-content-center flex-shrink-0 multiselect-arrow-container">
                                                        <DownArrowIc width={10} height={10} fill="#6B7280" />
                                                    </div>
                                                </div>
                                                {errors[`level_persons_${level.id}`] && (
                                                    <div className="text-danger fs-11 fw-500 mt-1 px-1">{errors[`level_persons_${level.id}`]}</div>
                                                )}
                                                {activeDropdown.levelId === level.id && activeDropdown.type === 'persons' && (
                                                    <div className={`custom-dropdown-menu ${dropdownDirection === 'up' ? 'open-up' : ''}`}>
                                                        {getFilteredSuggestions(level).length > 0 ? (
                                                            getFilteredSuggestions(level).map((item) => (
                                                                <div
                                                                    key={item.value}
                                                                    className="custom-dropdown-item"
                                                                    onClick={() => {
                                                                        addPerson(level.id, item);
                                                                    }}
                                                                >
                                                                    {item.label}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="p-2 text-center text-muted dropdown-empty-text">
                                                                No options available
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="d-flex justify-content-center">
                                                <label className="custom-switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={level.canReject}
                                                        onChange={(e) => {
                                                            const updated = [...levels];
                                                            updated[idx].canReject = e.target.checked;
                                                            setLevels(updated);
                                                        }}
                                                    />
                                                    <span className="custom-switch-slider"></span>
                                                </label>
                                            </div>
                                            <div className="d-flex justify-content-center">
                                                <label className="custom-switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={level.commentsMandatory}
                                                        onChange={(e) => {
                                                            const updated = [...levels];
                                                            updated[idx].commentsMandatory = e.target.checked;
                                                            setLevels(updated);
                                                        }}
                                                    />
                                                    <span className="custom-switch-slider"></span>
                                                </label>
                                            </div>
                                            <div className="d-flex justify-content-center">
                                                <DeleteIc
                                                    className="cursor-pointer delete-icon-btn"
                                                    width={56}
                                                    height={56}
                                                    onClick={() => removeLevel(level.id)}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <button className="add-level-btn mt-4" onClick={addLevel}>
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7 1V13M1 7H13" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Add level
                            </button>
                        </>
                    )}
                </div>
            </div>

            <ToastContainer position="top-end">
                <Toast className="d-inline-block m-1" bg={snackbarStatus === true ? "success" : "danger"} show={showSnackbar} onClose={handleSnackbarClose} delay={3000} autohide>
                    <Toast.Body style={{ color: "#ffffff" }}>{snackbarMessage}</Toast.Body>
                </Toast>
            </ToastContainer>
        </div>
    );
};
